import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FormikProps } from 'formik'
import dayjs from 'dayjs'

import AppointementTemplate, { AppointementTemplateProps } from '../../templates/Appointement'
import { useHeaderProps } from '../../components/Header/useProps'
import Header from '../../components/Header'
import { actions, selectors } from '../../redux'
import AppointementForm, {
  AppointementFormProps,
  AppointementFormValues,
} from '../../components/forms/AppointementForm'
import { Screen } from '../../types/global'
import { useSagaTakeEvery } from '../../hooks/useSaga'
import { SelectProps } from '../../components/form/Select'

const AppoinementPageForm: React.FC<{
  appointment: NonNullable<
    NonNullable<ReturnType<typeof selectors.auth.getMe>['data']>['appointments']
  >[number]
}> = ({ appointment }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  // SELECTOR

  const getMe = useSelector(selectors.auth.getMe)
  const getStores = useSelector(selectors.app.getStores)
  const cancelAppointement = useSelector(selectors.app.cancelAppointement)

  // REF

  const $formik = useRef<FormikProps<AppointementFormValues>>(null)

  // STATE

  const [selectedStoreId, setSelectedStoreId] = useState<string>(appointment?.slot.store.id ?? '')

  const [selectedDate, setSelectedDate] = useState<string>(
    appointment?.slot.opening_hour ? dayjs(appointment?.slot.opening_hour).format('YYYY-MM-DD') : ''
  )

  //  EFFECT

  useSagaTakeEvery(
    actions.app.cancelAppointementSuccess,
    useCallback(() => {
      dispatch(actions.app.getStoresRequest({}))
    }, [dispatch])
  )

  // VAR

  const canMakeAppointement = useMemo(
    () =>
      (getMe.data?.numberAppointmentsLeftIsReturn ?? 0) > 0 ||
      (getMe.data?.numberAppointmentsLeftNotIsReturn ?? 0) > 0,
    [getMe.data?.numberAppointmentsLeftIsReturn, getMe.data?.numberAppointmentsLeftNotIsReturn]
  )

  const allStores = useMemo(() => getStores.data?.filter((store) => store.isAvailable), [
    getStores.data,
  ])

  const availableStores = useMemo(
    () =>
      getStores.data?.filter((store) => {
        if (store.isReturn) {
          return getMe.data?.numberAppointmentsLeftIsReturn ?? 0 > 0
        }

        return getMe.data?.numberAppointmentsLeftNotIsReturn ?? 0 > 0
      }),
    [getStores.data, getMe]
  )

  const selectedStore = useMemo(() => allStores?.find((store) => store.id === selectedStoreId), [
    allStores,
    selectedStoreId,
  ])

  const availablesDates = useMemo(
    () =>
      selectedStore?.slots
        ?.map((slot) => ({
          label: dayjs(slot.opening_hour).format('YYYY-MM-DD'),
          value: dayjs(slot.opening_hour).format('YYYY-MM-DD'),
        }))
        ?.filter(
          (option, index, array) =>
            index === array.findIndex((_option) => _option.value === option.value)
        ) ?? [],
    [selectedStore?.slots]
  )

  const availablesSlots: SelectProps['options'] = useMemo(
    () =>
      selectedStore?.slots
        ?.filter(
          (slot) =>
            dayjs(slot.opening_hour).format('YYYY-MM-DD') === selectedDate &&
            (slot.isAvailable || slot.id === appointment?.slot?.id)
        )
        ?.map((slot) => ({
          label: `${dayjs(slot.opening_hour).format('HH:mm')} - ${dayjs(slot.closing_hour).format(
            'HH:mm'
          )}`,
          value: slot.id,
          disabled: !slot.isAvailable,
        })) ?? [],
    [appointment, selectedDate, selectedStore?.slots]
  )

  // PROPS

  const formProps: AppointementFormProps = useMemo(() => {
    return {
      formik: {
        innerRef: $formik,
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
          firstName: Yup.string().required(),
          lastName: Yup.string().required(),
          email: Yup.string().required().email(),
          store: Yup.string().required(),
          date: Yup.string().required(),
          slot: Yup.string().required(),
        }),
        initialValues: {
          firstName: appointment?.first_name ?? getMe.data?.first_name ?? '',
          lastName: appointment?.last_name ?? getMe.data?.last_name ?? '',
          email: appointment?.email ?? getMe.data?.email ?? '',
          store: appointment?.slot.store.id ?? '',
          date: appointment?.slot.opening_hour
            ? dayjs(appointment?.slot.opening_hour).format('YYYY-MM-DD')
            : '',
          slot: appointment?.slot?.id ?? '',
        },
        onSubmit: (values) => {
          dispatch(actions.app.setAppointementStep({ ...values }))
          if (appointment?.id) {
            // TODO: useless ?
            dispatch(actions.app.setSelectedAppointementId(appointment!.id))
          }
          dispatch(
            actions.app.setScreen({
              screen: getMe.data?.disclaimer_accepted ? Screen.message : Screen.disclaimer,
            })
          )
        },
        onContextUpdate: (context) => {
          if (context.dirty) {
            if (context.values.store !== selectedStoreId) {
              console.log({ context: context.values.store, state: selectedStoreId })
              setSelectedStoreId(context.values.store)
              $formik.current?.setFieldValue('date', '')
              $formik.current?.setFieldValue('slot', '')
            }
            if (context.values.date !== selectedDate) {
              setSelectedDate(context.values.date)
              $formik.current?.setFieldValue('slot', '')
            }
          }
        },
      },
      fields: {
        firstName: {
          name: 'firstName',
          label: t('appointement.form.firstName.label'),
          placeholder: t('appointement.form.firstName.placeholder'),
          required: true,
          disabled: !!appointment,
        },
        lastName: {
          name: 'lastName',
          label: t('appointement.form.lastName.label'),
          placeholder: t('appointement.form.lastName.placeholder'),
          required: true,
          disabled: !!appointment,
        },
        email: {
          name: 'email',
          label: t('appointement.form.email.label'),
          placeholder: t('appointement.form.email.placeholder'),
          required: true,
          disabled: !!appointment,
        },
        store: {
          name: 'store',
          label: t('appointement.form.store.label'),
          placeholder: t('appointement.form.store.placeholder'),
          required: true,
          options:
            allStores?.map((store) => ({
              label: store.name,
              value: store.id,
              disabled: !availableStores?.some((availableStore) => availableStore.id === store.id),
            })) ?? [],
          disabled: !!appointment,
        },
        date: {
          name: 'date',
          label: t('appointement.form.date.label'),
          placeholder: t('appointement.form.date.placeholder'),
          required: true,
          disabled: !selectedStoreId || !!appointment,
          options:
            !appointment && !selectedDate
              ? availablesDates
              : [
                  {
                    label: selectedDate,
                    value: appointment?.slot.opening_hour,
                  },
                ],
          helperText:
            !(!selectedStoreId || !!appointment) && availablesDates?.length === 0
              ? t('appointement.form.slot.help.noAvailability')
              : '',
        },
        slot: {
          name: 'slot',
          label: t('appointement.form.slot.label'),
          placeholder: t('appointement.form.slot.placeholder'),
          required: true,
          disabled: !selectedDate || !!appointment,
          options: appointment?.slot.opening_hour
            ? [
                {
                  label: `${dayjs(appointment?.slot.opening_hour).format('HH:mm')} - ${dayjs(
                    appointment?.slot.closing_hour
                  ).format('HH:mm')}`,
                  value: appointment?.slot?.id,
                  disabled: true,
                },
              ]
            : availablesSlots,
          helperText:
            !(!selectedDate || !!appointment) && availablesSlots?.length === 0
              ? t('appointement.form.slot.help.noAvailability')
              : '',
        },
      },
      editMessageButton: appointment
        ? {
            text: t('appointement.form.edit.message'),
            onClick: () => {
              dispatch(actions.app.setSelectedAppointementId(appointment!.id))
              dispatch(
                actions.app.setScreen({
                  screen: Screen.message,
                })
              )
            },
          }
        : undefined,
      submitButton: {
        text: appointment
          ? t('appointement.form.submit.cancel')
          : t('appointement.form.submit.confirm'),
        type: appointment ? 'button' : 'submit',
        disabled: appointment ? cancelAppointement.pending : !canMakeAppointement,
        onClick: appointment
          ? () => {
              dispatch(
                actions.app.cancelAppointementRequest({
                  appointment: appointment.id,
                })
              )
            }
          : undefined,
      },
      submitErrors:
        !appointment && !canMakeAppointement
          ? [{ label: t('appointement.form.error.appointmentLimit') }]
          : undefined,
    }
  }, [
    availableStores,
    allStores,
    availablesDates,
    availablesSlots,
    canMakeAppointement,
    cancelAppointement.pending,
    appointment,
    dispatch,
    getMe.data?.disclaimer_accepted,
    getMe.data?.email,
    getMe.data?.first_name,
    getMe.data?.last_name,
    selectedDate,
    selectedStoreId,
    t,
  ])

  // EFFECT

  useEffect(() => {
    setSelectedStoreId(appointment?.slot.store.id ?? '')
  }, [appointment?.slot.store.id])

  useEffect(() => {
    setSelectedDate(
      appointment?.slot.opening_hour
        ? dayjs(appointment?.slot.opening_hour).format('YYYY-MM-DD')
        : ''
    )
  }, [appointment?.slot.opening_hour])

  // SAGA

  useSagaTakeEvery(
    actions.app.cancelAppointementSuccess,
    useCallback(
      ({ payload }) => {
        if (payload.id === appointment?.id) {
          $formik.current!.resetForm({
            values: {
              firstName: getMe.data?.first_name ?? '',
              lastName: getMe.data?.last_name ?? '',
              email: getMe.data?.email ?? '',
              store: '',
              date: '',
              slot: '',
            },
          })
        }
      },
      [appointment?.id, getMe.data?.email, getMe.data?.first_name, getMe.data?.last_name]
    )
  )

  return <AppointementForm {...formProps} />
}

const AppointementPage: React.FC = () => {
  const { t } = useTranslation()

  // SELECTOR

  const getMe = useSelector(selectors.auth.getMe)

  const canMakeAppointement = useMemo(
    () =>
      (getMe.data?.numberAppointmentsLeftIsReturn ?? 0) > 0 ||
      (getMe.data?.numberAppointmentsLeftNotIsReturn ?? 0) > 0,
    [getMe.data?.numberAppointmentsLeftIsReturn, getMe.data?.numberAppointmentsLeftNotIsReturn]
  )

  const currentAppointments = useMemo(
    () =>
      getMe.data?.appointments?.filter((appointment) =>
        dayjs().isBefore(dayjs(appointment!.slot.closing_hour))
      ) ?? [],

    [getMe.data?.appointments]
  )

  const [newFormVisible, setNewFormVisible] = useState(false)

  // PROPS

  const headerProps = useHeaderProps()

  const templateProps: AppointementTemplateProps = useMemo(() => {
    return {
      layoutProps: {
        header: <Header {...headerProps} />,
      },
      title: t('appointement.title'),
      forms: (
        <>
          {[
            ...currentAppointments,
            ...(newFormVisible || currentAppointments.length === 0 ? [null] : []),
          ]?.map((appointment, index) => (
            <AppoinementPageForm key={index} appointment={appointment!} />
          ))}
        </>
      ),
      newAppointmeentButton:
        canMakeAppointement && (currentAppointments.length === 0 || !newFormVisible)
          ? {
              text: t('appointement.newAppointment'),
              onClick: () => {
                setNewFormVisible(true)
              },
            }
          : undefined,
    }
  }, [canMakeAppointement, currentAppointments, headerProps, newFormVisible, t])

  // RETURN

  if (!getMe.data) {
    return null
  }

  return <AppointementTemplate {...templateProps} />
}

export default AppointementPage
